import React from 'react';
import styled, { css } from 'styled-components';

import Modal from '../../modal/Modal';
import MediaPreview from '../MediaPreview';
import PrecisionAudioPlayer from '../../audio-preview/PrecisionAudioPlayer';
import { MediaType } from '../../../types/media-preview';
import { AcrScanStatus, AcrScanResult } from '../../../types/acr';
import {
  computeImageFit,
  ImageFitResult,
  PREVIEW_AREA_CHROME_PX,
} from './imageFit';

export interface MediaPreviewModalProps {
  src: string;
  mediaType: MediaType;
  onClose: () => void;
  fileName?: string;
  waveformUrl?: string;
  fileId?: string;
  acrScanStatus?: AcrScanStatus;
  acrScanResult?: AcrScanResult | null;
  acrScannedAt?: string | null;
  fileSize?: number;
  createdAt?: string;
  onDownload?: () => void;
  downloadUrl?: string;
  /**
   * 画像のとき、モーダルの幅を画像の表示サイズにぴったり合わせる（余白なし）。
   * 開いた時点でビューポートに応じた最大幅まで拡大して表示する（元画像の幅が
   * 上限・Modal size="auto-viewport"）。縦に収まらない分は縦スクロールで見せ、
   * 元画像がさらに広いときはクリックで等倍（100%）と最大幅を切り替えられる
   * （このときモーダルの大きさは変わらず、中のスクロールだけが変わる）。
   * 本文幅に縮められた画像を大きく見せる用途。既定 false（従来の固定幅）。
   */
  fitToImage?: boolean;
}

/** 非画像（動画・PDF 等）プレビューの最大高さ */
const PREVIEW_MAX_HEIGHT = 'calc(80vh - 120px)';

/**
 * プレビューのスクロール領域。
 * fitToImage の画像ではビューポート基準の高さ上限（imageFit の定数と対応）にし、
 * はみ出す画像（縦に長い画像・等倍表示）の左端・上端が切れないよう
 * justify-content ではなく子の margin: auto で中央寄せする（safe center 相当）。
 */
const PreviewArea = styled.div<{ $fitImage: boolean }>`
  display: flex;
  overflow: auto;
  ${({ $fitImage }) =>
    $fitImage
      ? css`
          max-height: calc(100dvh - ${PREVIEW_AREA_CHROME_PX}px);

          & > * {
            margin: auto;
          }
        `
      : css`
          justify-content: center;
          align-items: center;
          max-height: calc(100dvh - 64px);
        `}
`;

/**
 * fitToImage の画像を包み、表示幅の明示と等倍切り替えクリックを受ける。
 * flex: none で PreviewArea（flex）に縮められず、はみ出した分はスクロールになる。
 */
const ZoomWrapper = styled.div<{ $zoomCursor?: 'zoom-in' | 'zoom-out' }>`
  flex: none;
  ${({ $zoomCursor }) => ($zoomCursor ? `cursor: ${$zoomCursor};` : '')}
`;

/**
 * fitToImage 用: 元画像の幅とビューポート幅から表示幅を求める。
 * 中の ImagePreview は幅 100% で描画するため、縦横比どおりの幅を先に決めて
 * ラッパーに渡す。読み込み前（元画像の幅が不明な間）は null。
 */
const useImageFit = (
  src: string,
  enabled: boolean,
): (ImageFitResult & { naturalWidth: number }) | null => {
  const [naturalWidth, setNaturalWidth] = React.useState<number | null>(null);
  const [viewportWidth, setViewportWidth] = React.useState(
    () => window.innerWidth,
  );

  React.useEffect(() => {
    if (!enabled) return;
    setNaturalWidth(null);
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled && img.naturalWidth > 0) {
        setNaturalWidth(img.naturalWidth);
      }
    };
    img.src = src;
    return () => {
      cancelled = true;
    };
  }, [src, enabled]);

  React.useEffect(() => {
    if (!enabled) return;
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [enabled]);

  if (!enabled || naturalWidth === null) return null;
  return { ...computeImageFit(naturalWidth, viewportWidth), naturalWidth };
};

/**
 * メディアプレビューモーダル（共有コンポーネント）
 *
 * 画像・動画・PDF・音声のプレビューを表示。動画はネイティブ controls ではなく
 * カスタム操作（クリック停止・中央オーバーレイ・再開）を使用。
 * MediaPreview のクリックプレビューと FolderContentsModal の View で使用。
 */
const MediaPreviewModal: React.FC<MediaPreviewModalProps> = ({
  src,
  mediaType,
  onClose,
  fileName,
  waveformUrl,
  fileId,
  acrScanStatus,
  acrScanResult,
  acrScannedAt,
  fileSize,
  createdAt,
  onDownload,
  downloadUrl,
  fitToImage = false,
}) => {
  const isVideo = mediaType === 'video';
  const isYouTube = mediaType === 'youtube';
  const isAudio = mediaType === 'audio';
  const fitsImage = fitToImage && mediaType === 'image';
  const imageFit = useImageFit(src, fitsImage);

  const previewAreaRef = React.useRef<HTMLDivElement>(null);
  const zoomWrapperRef = React.useRef<HTMLDivElement>(null);
  /** クリック位置（画像内の比率）。等倍切り替え直後のスクロール合わせに使う */
  const pendingZoomOrigin = React.useRef<{ rx: number; ry: number } | null>(
    null,
  );

  // 等倍（100%）と最大幅表示の切り替え。縮小表示されている画像のみ有効。
  const [zoom, setZoom] = React.useState<'max' | 'actual'>('max');
  React.useEffect(() => {
    setZoom('max');
    // 同じモーダルで src が替わったとき、前の画像のスクロール位置を持ち越さない
    previewAreaRef.current?.scrollTo(0, 0);
  }, [src]);

  const isActualSize = Boolean(imageFit?.canZoom) && zoom === 'actual';

  const handleZoomToggle = (e: React.MouseEvent<HTMLDivElement>) => {
    const wrapper = zoomWrapperRef.current;
    if (wrapper) {
      const rect = wrapper.getBoundingClientRect();
      pendingZoomOrigin.current = {
        rx: (e.clientX - rect.left) / rect.width,
        ry: (e.clientY - rect.top) / rect.height,
      };
    }
    setZoom((prev) => (prev === 'actual' ? 'max' : 'actual'));
  };

  // 切り替え直後、クリックした位置が画面中央付近に来るようスクロールを合わせる
  // （最大幅へ戻るときはブラウザが可動範囲にクランプするのでそのまま動く）
  React.useLayoutEffect(() => {
    const origin = pendingZoomOrigin.current;
    pendingZoomOrigin.current = null;
    if (!origin) return;
    const area = previewAreaRef.current;
    const wrapper = zoomWrapperRef.current;
    if (!area || !wrapper) return;
    area.scrollLeft = origin.rx * wrapper.clientWidth - area.clientWidth / 2;
    area.scrollTop = origin.ry * wrapper.clientHeight - area.clientHeight / 2;
  }, [zoom]);

  const preview = (
    <MediaPreview
      src={src}
      mediaType={mediaType}
      sizeMode={{
        mode: 'auto',
        maxWidth: '100%',
        // fitsImage は高さを制約せず、収まらない分は PreviewArea のスクロールで見せる
        maxHeight: fitsImage ? 'none' : PREVIEW_MAX_HEIGHT,
        objectFit: 'contain',
      }}
      style={fitsImage ? { maxHeight: 'none' } : undefined}
      controls={mediaType === 'pdf' || isYouTube}
      autoPlay={isVideo || isYouTube}
      showDownloadIcon={false}
      disableHoverEffect={true}
      showMenu={!isYouTube}
      menuOutside={true}
      showProgressBar={isVideo}
      showRemainingTimeOnPlay={isVideo}
      showRestartOnPause={isVideo}
      fileName={fileName}
      fileSize={fileSize}
      createdAt={createdAt}
      fileId={fileId}
      acrScanStatus={acrScanStatus}
      acrScanResult={acrScanResult}
      acrScannedAt={acrScannedAt}
      onDownload={onDownload}
      downloadUrl={downloadUrl}
    />
  );

  const renderPreview = () => {
    if (!fitsImage) return preview;
    // 縦横比を読み込むまでは出さない（幅が決まらず一瞬広がるのを防ぐ）
    if (imageFit === null) return null;
    const displayWidth = isActualSize ? imageFit.naturalWidth : imageFit.width;
    // 表示幅を明示し、モーダル（auto-viewport）を画像の大きさに合わせる。
    // 等倍表示でははみ出した分を PreviewArea のスクロールで見せる
    // （canZoom 時はモーダルが既に最大幅なので、切り替えでモーダルは動かない）
    return (
      <ZoomWrapper
        ref={zoomWrapperRef}
        style={{
          width: displayWidth,
          maxWidth: isActualSize ? undefined : '100%',
        }}
        $zoomCursor={
          imageFit.canZoom ? (isActualSize ? 'zoom-out' : 'zoom-in') : undefined
        }
        onClick={imageFit.canZoom ? handleZoomToggle : undefined}
      >
        {preview}
      </ZoomWrapper>
    );
  };

  return (
    <Modal
      onClose={onClose}
      variant="dialog-glass"
      size={fitsImage ? 'auto-viewport' : 'default'}
      titleSize="compact"
      title={fileName || 'プレビュー'}
    >
      <PreviewArea ref={previewAreaRef} $fitImage={fitsImage}>
        {isAudio ? (
          // 音声はプレシジョンプレビューを直接描画する。
          // ACR バッジは遷移元の行にもあるためモーダル内では非表示。
          // 三点リーダーは文字起こし等の入口としてモーダル内でも表示する
          <PrecisionAudioPlayer
            src={src}
            waveformUrl={waveformUrl}
            fileName={fileName}
            downloadUrl={downloadUrl}
            onDownload={onDownload}
            showMenu
            showAcrBadge={false}
            fileId={fileId}
            acrScanStatus={acrScanStatus}
            acrScanResult={acrScanResult}
            acrScannedAt={acrScannedAt}
          />
        ) : (
          renderPreview()
        )}
      </PreviewArea>
    </Modal>
  );
};

export default MediaPreviewModal;
